import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/TextArea";
import { Label } from "@/components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Checkbox } from "@/components/ui/CheckBox";
import { Save, Loader2 } from "lucide-react";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import {
  useGetFormVersionQuery,
  useSubmitFormMutation,
} from "@/services/FormBuilderApi";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/UseToast";

// ============================================================================
// TYPES
// ============================================================================

interface Field {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  options?: string[];
  conditions?: Condition[];
  min?: number;
  max?: number;
  startDateTime?: string;
  endDateTime?: string;
  rows?: number;
  accept?: string;
  order?: number;
  apiIntegration?: ApiIntegration;
  sectionId?: string;
  sectionTitle?: string;
  layout?: {
    columns: number;
    order: number;
  };
}

interface Condition {
  id: string;
  sourceField: string;
  operator: string;
  value?: string;
  action: string;
}

interface ApiIntegration {
  enabled: boolean;
  endpoint: string;
  method: string;
  token?: string;
  responseMappings: ResponseMapping[];
}

interface ResponseMapping {
  id: string;
  responsePath: string;
  targetField: string;
}

interface GeneratedForm {
  id?: string;
  title: string;
  description: string;
  slug?: string;
  status?: string;
  fields: Field[];
}

// ============================================================================
// CONDITIONAL LOGIC EVALUATION
// ============================================================================

const evaluateFieldConditions = (
  field: Field,
  formData: Record<string, any>
): { shouldShow: boolean; isRequired: boolean } => {
  if (!field.conditions || field.conditions.length === 0) {
    return {
      shouldShow: true,
      isRequired: field.required || false,
    };
  }

  let shouldShow = true;
  let isRequired = field.required || false;

  field.conditions.forEach((condition) => {
    const sourceValue = formData[condition.sourceField];
    let conditionMet = false;

    switch (condition.operator) {
      case "equals":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length === 1 && sourceValue[0] === condition.value
          : sourceValue === condition.value;
        break;
      case "not_equals":
        conditionMet = Array.isArray(sourceValue)
          ? !(sourceValue.length === 1 && sourceValue[0] === condition.value)
          : sourceValue !== condition.value;
        break;
      case "includes":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.includes(condition.value)
          : sourceValue?.toString().includes(condition.value);
        break;
      case "not_includes":
        conditionMet = Array.isArray(sourceValue)
          ? !sourceValue.includes(condition.value)
          : !sourceValue?.toString().includes(condition.value);
        break;
      case "is_empty":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length === 0
          : !sourceValue || sourceValue.toString().trim() === "";
        break;
      case "is_not_empty":
        conditionMet = Array.isArray(sourceValue)
          ? sourceValue.length > 0
          : sourceValue && sourceValue.toString().trim() !== "";
        break;
    }

    if (conditionMet) {
      switch (condition.action) {
        case "show":
          shouldShow = true;
          break;
        case "hide":
          shouldShow = false;
          break;
        case "require":
          isRequired = true;
          break;
        case "optional":
          isRequired = false;
          break;
      }
    } else {
      if (condition.action === "show") {
        shouldShow = false;
      } else if (condition.action === "hide") {
        shouldShow = true;
      }
    }
  });

  return { shouldShow, isRequired };
};

// ============================================================================
// FIELD RENDERER COMPONENT
// ============================================================================

const FieldRenderer = ({
  field,
  formData,
  onInputChange,
  onApiTrigger,
}: {
  field: Field;
  formData: Record<string, any>;
  onInputChange: (fieldId: string, value: any, isFile?: boolean) => void;
  onApiTrigger: (fieldId: string, value: any) => void;
}) => {
  switch (field.type) {
    case "radio":
      return (
        <RadioGroup
          value={formData[field.id] || ""}
          onValueChange={(value) => {
            onInputChange(field.id, value);
            onApiTrigger(field.id, value);
          }}
        >
          {field.options?.map((option, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <RadioGroupItem value={option} id={`${field.id}-${idx}`} />
              <Label
                htmlFor={`${field.id}-${idx}`}
                className="font-normal cursor-pointer"
              >
                {option}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );

    case "select":
      return (
        <>
          <Select
            value={formData[field.id] || ""}
            onValueChange={(value) => {
              onInputChange(field.id, value);
              onApiTrigger(field.id, value);
            }}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={field.placeholder || "Select an option"}
              />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option, idx) => (
                <SelectItem key={idx} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "checkbox":
      return (
        <>
          <div className="space-y-2">
            {field.options?.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.id}-${idx}`}
                  checked={(formData[field.id] || []).includes(option)}
                  onCheckedChange={(checked) => {
                    const current = formData[field.id] || [];
                    const newValue = checked
                      ? [...current, option]
                      : current.filter((v: any) => v !== option);
                    onInputChange(field.id, newValue);
                    onApiTrigger(field.id, newValue);
                  }}
                />
                <Label
                  htmlFor={`${field.id}-${idx}`}
                  className="font-normal cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "textarea":
      return (
        <>
          <Textarea
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => onInputChange(field.id, e.target.value)}
            onBlur={() => onApiTrigger(field.id, formData[field.id])}
            rows={field.rows || 4}
            className="resize-none"
          />
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    case "file":
      return (
        <>
          <Input
            type="file"
            placeholder={field.placeholder}
            onChange={(e) => onInputChange(field.id, e.target.files?.[0], true)}
            onBlur={() => onApiTrigger(field.id, formData[field.id]?.name)}
            accept={field.accept}
          />
          {formData[field.id] && (
            <p className="text-sm text-gray-500">
              Selected file: {formData[field.id].name}
            </p>
          )}
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );

    default:
      return (
        <>
          <Input
            type={field.type === "datetime" ? "datetime-local" : field.type}
            placeholder={field.placeholder}
            value={formData[field.id] || ""}
            onChange={(e) => onInputChange(field.id, e.target.value)}
            onBlur={() => onApiTrigger(field.id, formData[field.id])}
            min={field.min}
            max={field.max}
            required={field.required}
          />
          {field.helpText && (
            <p className="text-sm text-gray-500">{field.helpText}</p>
          )}
        </>
      );
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FormRenderer = () => {
  useDocumentTitle("Form");
  const [searchParams] = useSearchParams();
  const service = searchParams.get("service");
  const { slug, } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [generatedForm, setGeneratedForm] = useState<GeneratedForm | null>(
    null
  );
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [sections, setSections] = useState<
    Record<string, { title: string; collapsed: boolean }>
  >({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const { data: formFromApi, isLoading } = useGetFormVersionQuery(slug!, {
    skip: !slug,
  });
  const [submitForm, { isLoading: isSubmitting }] = useSubmitFormMutation();

  // Load form from API
  useEffect(() => {
    if (formFromApi && slug) {
      // Build sections state from API data
      const sectionsMap: Record<string, { title: string; collapsed: boolean }> =
        {};
      formFromApi.fields.forEach((field) => {
        if (field.section_id && !sectionsMap[field.section_id]) {
          sectionsMap[field.section_id] = {
            title: field.section_title || "Untitled Section",
            collapsed: false,
          };
        }
      });
      setSections(sectionsMap);

      setGeneratedForm({
        id: formFromApi.id,
        title: formFromApi.name,
        description: formFromApi.description || "",
        slug: formFromApi.slug,
        status: formFromApi.status,
        fields: [...formFromApi.fields]
          .sort((a: any, b: any) => a.order - b.order)
          .map((field) => ({
            id: field.field_id || field.id.toString(),
            type: field.type,
            label: field.label,
            placeholder: field.placeholder || "",
            helpText: field.helper_text || "",
            required: field.required || false,
            options: field.options || [],
            conditions: field.conditions || [],
            min: field.min ?? undefined,
            max: field.max ?? undefined,
            startDateTime: field.start_date_time || undefined,
            endDateTime: field.end_date_time || undefined,
            rows: field.rows ?? undefined,
            accept: field.accept || undefined,
            order: field.order,
            sectionId: field.section_id || undefined,
            sectionTitle: field.section_title || undefined,
            layout: field.layout || undefined,
            apiIntegration: field.api_integration || {
              enabled: false,
              endpoint: "",
              method: "GET",
              token: "",
              responseMappings: [],
            },
          })),
      } as any);
    }
  }, [formFromApi, slug]);

  const handleInputChange = (fieldId: string, value: any, isFile = false) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleApiTrigger = async (fieldId: string, value: any) => {
    const field = generatedForm?.fields.find((f) => f.id === fieldId);
    if (!field?.apiIntegration?.enabled || !value) return;

    const targetFieldIds =
      field.apiIntegration.responseMappings
        ?.map((mapping) => mapping.targetField)
        .filter((id): id is string => Boolean(id)) || [];

    const clearMappedValues = () => {
      if (targetFieldIds.length === 0) return;

      setFormData((prev) => {
        const next = { ...prev };
        targetFieldIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });

      setValidationErrors((prev) => {
        const next = { ...prev };
        targetFieldIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    };

    try {
      const endpoint = field.apiIntegration.endpoint.replace(
        "{value}",
        encodeURIComponent(value)
      );
      const response = await fetch(endpoint, {
        method: field.apiIntegration.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("userToken") ?? ""}`,
        },
      });

      if (!response.ok) throw new Error("API error");
      const data = await response.json();

      const newFormData = { ...formData };
      const fieldsToClearErrors: string[] = [];

      field.apiIntegration.responseMappings.forEach((mapping) => {
        if (!mapping.targetField) return;

        const rawPath = mapping.responsePath?.trim();
        if (!rawPath) return;

        let val: any;

        // Allow static values by wrapping the desired string in double quotes
        if (rawPath.startsWith(`"`) && rawPath.endsWith(`"`)) {
          val = rawPath.slice(1, -1);
        } else {
          const source = Array.isArray(data) ? data[0] : data;
          val = source?.[rawPath];
        }

        if (val !== undefined) {
          newFormData[mapping.targetField] = val;
          // Track fields that were populated to clear their validation errors
          fieldsToClearErrors.push(mapping.targetField);
        }
      });

      setFormData(newFormData);

      // Clear validation errors for fields populated by API mapping
      if (fieldsToClearErrors.length > 0) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          fieldsToClearErrors.forEach((fieldId) => {
            delete newErrors[fieldId];
          });
          return newErrors;
        });
      }
    } catch (error) {
      clearMappedValues();
      console.error("API integration error:", error);
    }
  };

  const validateForm = (): { isValid: boolean; errors: Record<string, string> } => {
    if (!generatedForm) return { isValid: false, errors: {} };

    const errors: Record<string, string> = {};
    let isValid = true;

    generatedForm.fields.forEach((field) => {
      const { shouldShow, isRequired } = evaluateFieldConditions(
        field,
        formData
      );

      // Only validate fields that are visible
      if (!shouldShow) return;

      const value = formData[field.id];

      // Check if field is empty based on type
      let isEmpty = false;

      if (field.type === "checkbox") {
        isEmpty = !value || (Array.isArray(value) && value.length === 0);
      } else if (field.type === "file") {
        isEmpty = !value || !(value instanceof File);
      } else if (field.type === "radio" || field.type === "select") {
        isEmpty = !value || value.toString().trim() === "";
      } else {
        isEmpty =
          value === undefined ||
          value === null ||
          value.toString().trim() === "";
      }

      // Required field validation
      if (isRequired && isEmpty) {
        errors[field.id] = `${field.label} is required`;
        isValid = false;
        return; // Skip other validations if field is empty and required
      }

      // Skip other validations if field is empty but not required
      if (isEmpty) return;

      // Type-specific validations
      switch (field.type) {
        case "number":
          const numValue = parseFloat(value);

          if (isNaN(numValue)) {
            errors[field.id] = `${field.label} must be a valid number`;
            isValid = false;
          } else {
            // Min validation
            if (field.min !== undefined && numValue < field.min) {
              errors[field.id] = `${field.label} must be at least ${field.min}`;
              isValid = false;
            }
            // Max validation
            if (field.max !== undefined && numValue > field.max) {
              errors[field.id] = `${field.label} must be at most ${field.max}`;
              isValid = false;
            }
          }
          break;

        case "text":
        case "textarea":
          const textValue = value.toString();

          // Min length validation (using min as character count)
          if (field.min !== undefined && textValue.length < field.min) {
            errors[field.id] = `${field.label} must be at least ${field.min} characters`;
            isValid = false;
          }
          // Max length validation (using max as character count)
          if (field.max !== undefined && textValue.length > field.max) {
            errors[field.id] = `${field.label} must be at most ${field.max} characters`;
            isValid = false;
          }
          break;

        case "date":
        case "datetime":
          const dateValue = new Date(value);

          if (isNaN(dateValue.getTime())) {
            errors[field.id] = `${field.label} must be a valid date`;
            isValid = false;
          } else {
            // Min date validation
            if (field.startDateTime) {
              let minDate = new Date(field.startDateTime);
              if (field.type === "date") {
                // For date fields, compare against the date part only (UTC midnight)
                minDate = new Date(field.startDateTime.split("T")[0]);
              }

              if (dateValue < minDate) {
                const dateDisplay =
                  field.type === "date"
                    ? field.startDateTime.split("T")[0]
                    : new Date(field.startDateTime).toLocaleString();
                errors[
                  field.id
                ] = `${field.label} must be on or after ${dateDisplay}`;
                isValid = false;
              }
            }
            // Max date validation
            if (field.endDateTime) {
              let maxDate = new Date(field.endDateTime);
              if (field.type === "date") {
                // For date fields, compare against the date part only (UTC midnight)
                maxDate = new Date(field.endDateTime.split("T")[0]);
              }

              if (dateValue > maxDate) {
                const dateDisplay =
                  field.type === "date"
                    ? field.endDateTime.split("T")[0]
                    : new Date(field.endDateTime).toLocaleString();
                errors[
                  field.id
                ] = `${field.label} must be on or before ${dateDisplay}`;
                isValid = false;
              }
            }
          }
          break;

        case "file":
          if (value instanceof File) {
            // File type validation using accept attribute
            if (field.accept) {
              const acceptedTypes = field.accept.split(',').map(t => t.trim());
              const fileExtension = '.' + value.name.split('.').pop()?.toLowerCase();
              const fileMimeType = value.type;

              const isAccepted = acceptedTypes.some(acceptType => {
                // Check if it's a MIME type (e.g., "image/*", "application/pdf")
                if (acceptType.includes('/')) {
                  if (acceptType.endsWith('/*')) {
                    const baseType = acceptType.split('/')[0];
                    return fileMimeType.startsWith(baseType + '/');
                  }
                  return fileMimeType === acceptType;
                }
                // Check if it's a file extension (e.g., ".pdf", ".jpg")
                return fileExtension === acceptType.toLowerCase();
              });

              if (!isAccepted) {
                errors[field.id] = `${field.label} must be one of: ${field.accept}`;
                isValid = false;
              }
            }
          }
          break;
      }
    });

    setValidationErrors(errors);
    return { isValid, errors };
  };

  const handleSubmitForm = async () => {
    if (!generatedForm) return;

    // Validate form before submission
    const validation = validateForm();
    if (!validation.isValid) {
      // Scroll to first error
      const firstErrorField = Object.keys(validation.errors)[0];
      if (firstErrorField) {
        // Use setTimeout to ensure state is updated and DOM is ready
        setTimeout(() => {
          const errorElement = document.querySelector(
            `[data-field-id="${firstErrorField}"]`
          );
          errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
      return;
    }

    const files: File[] = [];
    Object.entries(formData).map(([key, value]) => {
      // Check if value is a File
      if (value instanceof File) {
        files.push(value);
        formData[key] = value.name;
      }
      // Check if value is FileList (multiple files from input)
      else if (value instanceof FileList) {
        Array.from(value).forEach(file => files.push(file));
        formData[key] = Array.from(value).map(f => f.name).join(', ');
      }
      // For regular values (handle objects/arrays if needed)
      else if (typeof value === 'object' && value !== null) {
        formData[key] = JSON.stringify(value);
      }
    });

    const payload = {
      form_id: generatedForm.id,
      data: formData,
      source: "service",
      source_id: service || "",
      files: files,
    };

    try {
      const submitData = await submitForm(payload).unwrap();
      if (submitData) {
        toast({
          variant: "success",
          title: "Form Submitted",
          description: "Your responses have been recorded successfully.",
        });
        navigate('/');
      }
    } catch (error: any) {
      console.error("Form submission error:", error);
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: "An error occurred while submitting the form. Please try again.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 flex overflow-hidden">
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="max-w-6xl mx-auto p-6">
          {generatedForm ? (
            <Card>
              <CardHeader className="bg-primary text-white">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-2xl font-bold">{generatedForm.title}</p>
                    <p className="text-sm text-white/90 mt-1">
                      {generatedForm.description}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 min-h-[400px]">
                {generatedForm.fields.length === 0 ? (
                  <div className="flex items-center justify-center h-[400px] text-gray-500">
                    <p>No fields in this form</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(() => {
                      // Group fields by section while preserving order
                      const sectionOrder: string[] = [];
                      const groupedBySection = generatedForm.fields.reduce(
                        (acc, field) => {
                          const key = field.sectionId || "ungrouped";
                          if (!acc[key]) {
                            acc[key] = {
                              title:
                                field.sectionTitle ||
                                sections[key]?.title ||
                                "Untitled Section",
                              fields: [],
                            };
                            if (!sectionOrder.includes(key)) {
                              sectionOrder.push(key);
                            }
                          }
                          acc[key].fields.push(field);
                          return acc;
                        },
                        {} as Record<string, { title: string; fields: Field[] }>
                      );

                      // Ensure ungrouped fields appear last if they exist
                      if (sectionOrder.includes("ungrouped")) {
                        sectionOrder.push(
                          sectionOrder.splice(
                            sectionOrder.indexOf("ungrouped"),
                            1
                          )[0]
                        );
                      }

                      return Object.entries(groupedBySection)
                        .sort(([keyA], [keyB]) => {
                          return (
                            sectionOrder.indexOf(keyA) -
                            sectionOrder.indexOf(keyB)
                          );
                        })
                        .map(([sectionId, group]) => {
                          const isCollapsed =
                            sections[sectionId]?.collapsed ?? false;

                          return (
                            <div key={sectionId} className="mb-12">
                              {/* Section Header */}
                              {sectionId !== "ungrouped" && (
                                <div
                                  className="flex items-center justify-between p-5 bg-primary/5 rounded-lg cursor-pointer hover:bg-primary/10 transition mb-6 border border-primary/20 select-none"
                                  onClick={() =>
                                    setSections((prev) => ({
                                      ...prev,
                                      [sectionId]: {
                                        ...prev[sectionId],
                                        collapsed: !prev[sectionId]?.collapsed,
                                      },
                                    }))
                                  }
                                >
                                  <h3 className="font-semibold text-lg text-primary">
                                    {group.title}
                                  </h3>
                                  <span className="text-sm text-gray-600">
                                    {group.fields.length} field
                                    {group.fields.length !== 1 ? "s" : ""}
                                    {isCollapsed ? " ▸" : " ▾"}
                                  </span>
                                </div>
                              )}

                              {!isCollapsed && (
                                <div
                                  className={
                                    sectionId !== "ungrouped" ? "pl-8" : ""
                                  }
                                >
                                  {/* Row Grouping Inside Section */}
                                  {(() => {
                                    const rows: Field[][] = [];
                                    let currentRow: Field[] = [];

                                    group.fields.forEach((field) => {
                                      const cols = field.layout?.columns || 1;

                                      if (cols === 1) {
                                        if (currentRow.length > 0) {
                                          rows.push(currentRow);
                                          currentRow = [];
                                        }
                                        rows.push([field]);
                                      } else {
                                        if (
                                          currentRow.length > 0 &&
                                          currentRow[0].layout?.columns !== cols
                                        ) {
                                          rows.push(currentRow);
                                          currentRow = [];
                                        }
                                        currentRow.push(field);
                                        if (currentRow.length === cols) {
                                          rows.push(currentRow);
                                          currentRow = [];
                                        }
                                      }
                                    });
                                    if (currentRow.length > 0)
                                      rows.push(currentRow);

                                    return rows.map((row, rowIndex) => {
                                      const columnCount =
                                        row[0]?.layout?.columns || 1;

                                      return (
                                        <div
                                          key={rowIndex}
                                          className={`grid gap-6 mb-6 ${columnCount === 1
                                            ? "grid-cols-1"
                                            : columnCount === 2
                                              ? "grid-cols-1 md:grid-cols-2"
                                              : columnCount === 3
                                                ? "grid-cols-1 md:grid-cols-3"
                                                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                                            }`}
                                        >
                                          {row.map((field) => {
                                            const { shouldShow, isRequired } =
                                              evaluateFieldConditions(
                                                field,
                                                formData
                                              );

                                            if (!shouldShow) return null;

                                            return (
                                              <div
                                                key={field.id}
                                                className="relative group"
                                                data-field-id={field.id}
                                              >
                                                <div className={`space-y-3 p-6 border rounded-lg bg-white ${validationErrors[field.id]
                                                  ? "border-red-500"
                                                  : ""
                                                  }`}>
                                                  <div className="flex items-start justify-between gap-2">
                                                    <Label className="text-base font-medium">
                                                      {field.label}
                                                      {isRequired && (
                                                        <span className="text-red-500 ml-1">
                                                          *
                                                        </span>
                                                      )}
                                                    </Label>
                                                  </div>
                                                  <FieldRenderer
                                                    field={field}
                                                    formData={formData}
                                                    onInputChange={
                                                      handleInputChange
                                                    }
                                                    onApiTrigger={
                                                      handleApiTrigger
                                                    }
                                                  />
                                                  {validationErrors[field.id] && (
                                                    <p className="text-sm text-red-500 mt-1">
                                                      {validationErrors[field.id]}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}

                                          {/* Fill empty cells */}
                                          {columnCount > row.length &&
                                            Array(columnCount - row.length)
                                              .fill(null)
                                              .map((_, i) => (
                                                <div key={`empty-${i}`} />
                                              ))}
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        });
                    })()}

                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={handleSubmitForm}
                        disabled={isSubmitting}
                        className="bg-primary hover:bg-primary-dark w-full"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Submit Form
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            < div className="flex items-center justify-center h-screen">
              <p className="text-gray-500">Form not available</p>
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default FormRenderer;
